import { useCallback, useEffect, useState } from 'react'

import { useAuth } from '@/api/auth'
import {
  applyTheme,
  getStoredTheme,
  loadThemeFromServer,
  resolveTheme,
  saveThemeToServer,
  setStoredTheme,
  type ThemePreference,
} from '@/lib/theme'

export interface ThemeState {
  preference: ThemePreference
  resolved: 'light' | 'dark'
  setPreference: (next: ThemePreference) => void
  syncing: boolean
}

export function useTheme(): ThemeState {
  const { user } = useAuth()
  const [preference, setPreferenceState] = useState<ThemePreference>(() => getStoredTheme())
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolveTheme(getStoredTheme()))
  const [syncing, setSyncing] = useState(false)

  useEffect(() => {
    applyTheme(preference)
    setResolved(resolveTheme(preference))

    if (preference !== 'system') return undefined

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (): void => setResolved(resolveTheme('system'))
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [preference])

  useEffect(() => {
    if (user === null) return undefined

    const controller = new AbortController()
    setSyncing(true)

    void loadThemeFromServer(controller.signal)
      .then((remote) => {
        if (remote === null) return
        setStoredTheme(remote)
        setPreferenceState(remote)
        setResolved(resolveTheme(remote))
      })
      .finally(() => {
        if (!controller.signal.aborted) setSyncing(false)
      })

    return () => controller.abort()
  }, [user])

  const setPreference = useCallback(
    (next: ThemePreference): void => {
      setStoredTheme(next)
      setPreferenceState(next)
      setResolved(resolveTheme(next))

      if (user === null) return

      void saveThemeToServer(next).catch(() => {
        // Local choice still applies; the next successful sync will retry.
      })
    },
    [user],
  )

  return { preference, resolved, setPreference, syncing }
}
